/**
 * Fetches the vendored skin CSS asset (`skin.css`, see `@econ-v1/ui`'s `SKIN_CSS_URL`/
 * `applySkinStyleSheet`) as plain text. Lives here, not in `client/ui`, because UI package
 * code may not perform browser I/O directly — `fetch` is a transport global gated to
 * adapters only (boundary rules B2/B3/N1, `client/scripts/check-boundaries.mjs`). The
 * composition root (`system/pwa/src/main.ts`) calls this directly (the same pattern as
 * `registerBrowserServiceWorker`/`createBrowserKernelHost`) and hands the resolved text to
 * `applySkinStyleSheet()`.
 *
 * Retries the fetch exactly once on failure (a transient network blip mid-boot — e.g. the
 * service worker hasn't started controlling this navigation yet — shouldn't fail the whole
 * shell after a single dropped request); a second failure rejects the returned promise.
 */
/**
 * #1679: every attempt is bounded by its own `AbortController`, so a STALLED response
 * (socket open, headers or body bytes never arriving) fails the attempt instead of hanging
 * forever. This matters because `system/pwa/src/main.ts` gates `replaceChildren(shell)` on
 * this promise: without a bound, a stall never rejects, so the one retry below never arms,
 * `renderSkinLoadFailure()` never runs, and the user sits on `#shell-loading` indefinitely
 * with no affordance to recover. Same failure shape as #1660/#1653 — an unbounded resource
 * acquisition producing a terminal wait rather than an error.
 *
 * 10s per attempt is chosen against the asset's real transfer cost, not picked round:
 * `skin.css` is ~83KB gzip, which on Chrome DevTools' slowest built-in throttle preset
 * ("Slow 3G": ~400kbps down, 2s RTT) completes in roughly 4s end-to-end. 10s leaves ~2.5x
 * headroom over that worst realistic profile, so a merely slow connection still boots
 * normally, while 10s is also the classic threshold past which a wait stops reading as
 * "loading" and starts reading as "broken". The bound covers `response.text()` as well as
 * the headers — aborting the signal errors the body stream too — because a stall that
 * delivers headers and then stops is exactly as terminal as one that never connects.
 *
 * Worst case is therefore two bounded attempts (~20s) before the fallback card renders,
 * versus unbounded today. The signal is created fresh per attempt so the retry gets its own
 * full budget rather than inheriting an already-expired one.
 */
export const SKIN_CSS_FETCH_TIMEOUT_MS = 10_000;
export async function fetchSkinCssText(url, options = {}) {
    const timeoutMs = options.timeoutMs ?? SKIN_CSS_FETCH_TIMEOUT_MS;
    try {
        return await fetchOnce(url, timeoutMs);
    }
    catch {
        return await fetchOnce(url, timeoutMs);
    }
}
async function fetchOnce(url, timeoutMs) {
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, timeoutMs);
    try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
            throw new Error(`skin stylesheet fetch failed: ${response.status} ${response.statusText}`);
        }
        return await response.text();
    }
    catch (error) {
        // Re-label the abort so a stall is distinguishable from a genuine network error in the
        // console line `renderSkinLoadFailure()` logs — both reach the same fallback card, but
        // only one of them means "the server accepted the connection and then went silent".
        if (timedOut) {
            throw new Error(`skin stylesheet fetch timed out after ${timeoutMs}ms`);
        }
        throw error;
    }
    finally {
        clearTimeout(timer);
    }
}
//# sourceMappingURL=browser-skin-css.js.map